const HERO_IMAGE =
  'https://images.unsplash.com/photo-1459865264687-595d652de67e?auto=format&fit=crop&w=1600&q=80';

/**
 * Featured guide: public and reservable soccer fields near Powell, Dublin,
 * Lewis Center, and Delaware, Ohio.
 */
export const OHIO_OPEN_SOCCER_FIELDS_POST = {
  id: 'featured-ohio-open-soccer-fields',
  slug: 'open-soccer-fields-powell-dublin-lewis-center-delaware-ohio',
  title: 'Open Soccer Fields Near Powell, Dublin, Lewis Center & Delaware (Ohio)',
  excerpt:
    'Where community teams practice and play—city parks, township complexes, and neighborhood open fields from Powell to Delaware. Free pickup spots, reservations, and permit-only sites explained.',
  publishedAt: '2026-07-21T22:00:00.000Z',
  coverImageUrl: HERO_IMAGE,
  authorName: 'Daily Sports Life',
  clubName: 'Ohio Field Guide',
  blogCategory: 'club-news',
  featured: true,
  component: 'WellnessGuideBlogArticle',
  theme: 'exercise',
  sections: [
    {
      type: 'stats',
      ariaLabel: 'Guide overview',
      items: [
        { value: '4 areas', label: 'Powell · Dublin · Lewis Center · Delaware' },
        { value: '25+', label: 'Listed field sites' },
        { value: 'Dawn–dusk', label: 'Most public parks' },
        { value: 'Check first', label: 'League & permit calendars' },
      ],
    },
    {
      type: 'panel',
      title: 'How to read this guide',
      paragraphs: [
        'This list is for adult and youth pickup groups, Indian community teams, and rec clubs looking for grass or turf in northern Delaware County and northwest Franklin County. Parks are generally open dawn to dusk unless posted otherwise.',
        '“Free & open” means you can walk on and kick around when the space is not reserved—no daily gate fee. Organized practices, recurring team sessions, leagues, and tournaments usually need a reservation or city permit even at public parks.',
        'Always check the field calendar, respect posted signs, and yield to permitted leagues. Goals should be anchored before play when nets are provided.',
      ],
      chips: ['Pickup friendly', 'Reserve for teams', 'Ohio 43015–43065', 'Updated July 2026'],
    },
    {
      type: 'table',
      title: 'Powell — City of Powell parks',
      intro:
        'Powell’s main soccer inventory is at Seldom Seen Park (five fields). Smaller green spaces work for small-sided passing. Athletic fields rent through Powell RecDesk; resident half-field soccer starts around $10 per 90 minutes.',
      headers: ['Field / park', 'Address', 'Access', 'Notes'],
      rows: [
        [
          'Seldom Seen Park — main soccer complex',
          '3240 Seldom Seen Rd, Powell, OH 43065',
          'Free & open when empty · Reserve for teams',
          'Five soccer fields, restrooms, playground, paths. Primary site for Powell rec soccer. Book via powell.recdesk.com.',
        ],
        [
          'Seldom Seen — Back 1 (small-sided)',
          '3232 Seldom Seen Rd (back lot)',
          'Reserve',
          'Unlined ~30×50 yd green space near wetlands—good for 5v5 or passing drills.',
        ],
        [
          'Library Park green space',
          '420 S Liberty St, Powell, OH 43065',
          'Free & open · Reserve for recurring use',
          'Open lawn just under regulation width—casual play when not booked.',
        ],
        [
          'Meadow View Park green space',
          '329 Ashmoore Dr, Powell, OH 43065',
          'Free & open · Reserve for recurring use',
          'Central flat area popular for small practices and picnics.',
        ],
        [
          'Arbor Ridge Park green space',
          '500 Bennett Pkwy, Powell, OH 43065',
          'Free & open · Reserve for recurring use',
          'Neighborhood open lawn for passing, juggling, and light scrimmages.',
        ],
        [
          'Adventure Park (multi-use lawn)',
          '260 Adventure Park Dr, Powell, OH 43065',
          'Free & open',
          'Smaller neighborhood park—best for skills, not full 11v11.',
        ],
      ],
    },
    {
      type: 'table',
      title: 'Dublin — City of Dublin sports parks',
      intro:
        'Dublin parks are open dawn to dusk. All organized soccer (leagues, camps, clinics, tryouts, recurring practices) requires a City of Dublin field permit. Dublin Soccer League (DSL) holds many spring windows at Avery Park—check before you set up.',
      headers: ['Field / park', 'Address', 'Access', 'Notes'],
      rows: [
        [
          'Avery Park soccer fields',
          '7401 Avery Rd, Dublin, OH 43016',
          'Free pickup when open · Permit for organized play',
          'Primary Dublin soccer park with multiple lined fields, restrooms, playground. DSL practices/games March–June.',
        ],
        [
          'Darree Fields',
          '6259 Cosgray Rd, Dublin, OH 43016',
          'Permit / tournament use',
          'Large multi-field complex—often reserved for tournaments and events. Not a casual daily pickup default.',
        ],
        [
          'Ted Kaltenbach Park soccer field',
          '5985 Cara Rd, Dublin, OH 43016',
          'Free & open when empty · Permit for organized play',
          'Grass field with goals; DSL lists as practice option. Community center and shelter nearby.',
        ],
        [
          'Coffman Park soccer field',
          '5600 Coffman Rd, Dublin, OH 43017',
          'Permit · Check schedule',
          'Central Dublin park—field shared with other sports. Avoid lacrosse-specific areas.',
        ],
        [
          'Emerald Fields / Emerald Park',
          'Darree Fields area, Dublin',
          'Mostly other sports',
          'City lists for rentals but Dublin Soccer League notes lacrosse/cricket priority—not ideal for soccer practice.',
        ],
        [
          'Earlington Park',
          'Near Dublin Jerome HS area',
          'Program / permit windows',
          'Often reserved for DSL Timbits and youth programs on weekends.',
        ],
      ],
    },
    {
      type: 'table',
      title: 'Lewis Center & Orange Township',
      intro:
        'Lewis Center sits across Orange Township and Olentangy schools. North Road Park is the region’s largest public soccer hub (seven fields). Jennings Sports Park is a paid private complex on turf.',
      headers: ['Field / park', 'Address', 'Access', 'Notes'],
      rows: [
        [
          'North Road Park — soccer complex',
          '5864 North Rd, Lewis Center, OH 43035',
          'Free & open dawn–dusk · Reserve for teams',
          'Seven soccer fields plus cricket, pickleball, shelter, restrooms, playground. Orange Township residents reserve fields via RecDesk.',
        ],
        [
          'North Orange Park soccer fields',
          '7560 Gooding Blvd, Delaware, OH 43015',
          'Free & open when empty · Reserve (residents)',
          'Multiple grass fields inside large community park with aquatic center, trails, and playground.',
        ],
        [
          'Jennings Sports Park (turf)',
          '5412 Piatt Rd, Lewis Center, OH 43035',
          'Paid rental',
          'Four synthetic multi-purpose fields—rent by the hour (~$150/hr). Tournaments, camps, and booked practices.',
        ],
        [
          'RO Park soccer courts',
          '6804 Snapdragon Way, Lewis Center, OH 43035',
          'Free & open',
          'Neighborhood park with small soccer courts—good for small-sided play and youth skills.',
        ],
        [
          'Olentangy HS practice fields',
          '675 Lewis Center Rd, Lewis Center, OH 43035',
          'School permit only',
          'Used by school and club programs—do not assume availability without permission.',
        ],
        [
          'Olentangy Orange HS fields',
          '2840 E Orange Rd, Lewis Center, OH 43035',
          'School permit only',
          'Same as other campus fields—check Olentangy or club schedules first.',
        ],
      ],
    },
    {
      type: 'table',
      title: 'Delaware — City of Delaware & community parks',
      intro:
        'Delaware Rec Soccer and Delaware Knights use Mingo Park, Smith Fields, Veterans Park, and smaller “community” neighborhood goals. Community parks are often the most pickup-friendly open spaces.',
      headers: ['Field / park', 'Address', 'Access', 'Notes'],
      rows: [
        [
          'Mingo Park multi-use fields',
          '500 E Lincoln Ave, Delaware, OH 43015',
          'Free & open · City programs',
          '50-acre city park with multi-use athletic areas, pool, tennis, skate park, river path. Primary Delaware city soccer hub.',
        ],
        [
          'Smith Fields',
          '1450 Troy Rd, Delaware, OH 43015',
          'League / permit windows',
          'Dedicated soccer layout used by Delaware Knights and rec leagues—confirm open times.',
        ],
        [
          'Veterans Park (behind YMCA)',
          '1121 S Houk Rd, Delaware, OH 43015',
          'League / permit windows',
          'Open fields behind the Delaware YMCA—popular for club practices when not scheduled.',
        ],
        [
          'Nottingham Park — community field',
          'Buehler Dr & Hyde Park Ln, Delaware, OH',
          'Free & open',
          'Smaller neighborhood “community park” field listed by Delaware Rec Soccer for open practices.',
        ],
        [
          'Sunnyview / PPG Park — community field',
          'Rock Creek Dr (off W William St), Delaware, OH',
          'Free & open',
          'Neighborhood open space—good for evening small-group sessions.',
        ],
        [
          'Blue Limestone Park — community field',
          'King Ave (off W William St), Delaware, OH',
          'Free & open',
          'Compact community goal area near west-side neighborhoods.',
        ],
        [
          'Lexington Glen / Locust Curve Park',
          'Providence Ln (off Lexington Blvd), Delaware, OH',
          'Free & open',
          'Residential park with open practice space used by local rec programs.',
        ],
      ],
    },
    {
      type: 'panel',
      title: 'Quick rules for community teams',
      list: [
        'Pick the closest “free & open” park for casual kickarounds; book or permit before recurring Tuesday/Thursday practices.',
        'Arrive 15 minutes early to claim space only if no permit sign or league is present—never argue with a scheduled group.',
        'Bring corner flags or cones if goals are not set; anchor portable goals per Ohio South / league safety rules.',
        'Parking on grass is banned at many Dublin and Powell sites—use lots even if it means a longer walk.',
        'Spring mud closes some Powell fields until early April; Dublin and Orange Township may rotate fields after heavy rain.',
      ],
    },
    {
      type: 'panel',
      title: 'Before you drive — 60-second checklist',
      orderedList: [
        'Search the park name + “field status” or open the city/township RecDesk calendar.',
        'Confirm dusk closing time (most close at dark).',
        'Check weather and lightning policy—Ohio leagues commonly pause 30 minutes after thunder.',
        'Text your group which lot to use (North Road and Avery fill up on weeknights).',
        'Pack balls, pennies, water, and a printed permit if your team has one.',
      ],
      callout:
        'Field rules change each season. Links below go to official parks departments—use them as the source of truth before planning a new practice slot.',
    },
  ],
  referenceLinks: [
    {
      label: 'City of Powell — facility rentals & RecDesk',
      url: 'https://www.cityofpowell.us/Residents/Parks-Recreation/Facility-Rentals',
    },
    {
      label: 'City of Dublin — sports field permits',
      url: 'https://dublinohiousa.gov/recreation-services/facility-rentals/sports-field-use-applications/',
    },
    {
      label: 'Orange Township parks (North Road & North Orange)',
      url: 'https://www.orangetwp.org/townshipparks/',
    },
    {
      label: 'City of Delaware parks & reservations',
      url: 'https://www.delawareohio.net/departments/parks_and_recreation',
    },
    {
      label: 'Jennings Sports Park — field rental',
      url: 'https://www.jenningssportspark.com/sports/soccer/',
    },
  ],
};
